/**
 * ResolveCodeDialog — "Add new Supplier Code" / unallocated-code resolution
 * workflow (v1.17).
 *
 * Opened when a line has a supplier code but no internal inventory code. The
 * user resolves it one of two ways:
 *   A. Match an existing internal code — search/select over the product catalog.
 *   B. Create a new product — opens AddProductDialog (pre-filled from the line),
 *      then auto-selects the freshly created code.
 *
 * The supplier is shown read-only throughout. A "Remember for <Supplier>"
 * checkbox (default on) controls whether the supplier-code -> internal-code
 * mapping is persisted for future auto-matching (per-supplier). The mapping is
 * written ONLY when the user confirms here — never on raw import.
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Autocomplete,
  TextField, Stack, Box, Typography, Checkbox, FormControlLabel, Divider, Chip,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import AddIcon from '@mui/icons-material/Add';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import LabelOffIcon from '@mui/icons-material/LabelOff';
import { BRAND, BRAND_HOVER, FI_ORANGE } from '../theme/tokens';
import { mintInternalCode } from '../utils/codeMatching';
import type { Product } from '../data/types';
import { AddProductDialog, AddProductDraft } from './AddProductDialog';

export interface ResolveCodeLine {
  supplierCode: string;
  description: string;
  unitPrice?: number;
}

export interface ResolveCodeDialogProps {
  open: boolean;
  supplierId: string;
  supplierName: string;
  line: ResolveCodeLine;
  products: Product[];
  onClose: () => void;
  /** Confirm a resolution. Caller sets the line's internalCode and, if remember, persists the SupplierCodeMap. */
  /** Company 3-letter prefix for minting (e.g. FIE → FIE-234). */
  internalCodePrefix?: string;
  onResolved: (internalCode: string, remember: boolean) => void;
  /** Create a new product in the catalog; returns the created product (with id). */
  onCreateProduct: (draft: AddProductDraft) => Product;
  /** One-shot: auto-match (3-letter prefix rule) or auto-create internal code, then link. */
  onAutoCreateAndMatch?: () => void;
  /** Mark line as a service (labour) line — no internal inventory code required. */
  onMarkService?: () => void;
}

export const ResolveCodeDialog: React.FC<ResolveCodeDialogProps> = ({
  open, supplierId, supplierName, line, products, internalCodePrefix, onClose, onResolved, onCreateProduct, onAutoCreateAndMatch, onMarkService,
}) => {
  // Supplier-agnostic: a brand-new internal item gets a freshly minted company
  // code (FIE-234), never the supplier's own code.
  const mintedCode = useMemo(() => mintInternalCode(products, internalCodePrefix), [products, internalCodePrefix, open]);
  const [selected, setSelected] = useState<Product | null>(null);
  const [remember, setRemember] = useState(true);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setSelected(null);
      setRemember(true);
      setAddOpen(false);
    }
  }, [open, line.supplierCode]);

  const categories = useMemo(
    () => Array.from(new Set(products.map(p => p.category).filter(Boolean))).sort(),
    [products]
  );

  const fmt = (n?: number) => (n != null ? `$${n.toFixed(2)}` : '—');

  const handleConfirm = () => {
    if (!selected) return;
    onResolved(selected.code, remember);
  };

  const handleProductCreated = (draft: AddProductDraft, addAnother: boolean) => {
    const created = onCreateProduct(draft);
    setSelected(created);
    // "Save and add another" keeps the AddProductDialog open (it resets itself);
    // a plain Save returns to the resolve dialog with the new code pre-selected.
    if (!addAnother) setAddOpen(false);
  };

  return (
    <>
      <Dialog open={open && !addOpen} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: '1.05rem' }}>
          <LinkIcon sx={{ color: BRAND }} /> Resolve supplier code
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 0.5 }}>
            {/* Supplier line context */}
            <Box sx={{ backgroundColor: '#fff5f5', border: '1px solid #ffcdd2', borderRadius: 1, p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.62rem', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>
                  Supplier
                </Typography>
                <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: BRAND }}>{supplierName}</Typography>
                <Chip label="Not editable" size="small" sx={{ height: 16, fontSize: '0.56rem', backgroundColor: '#ede7f6', color: BRAND }} />
              </Stack>
              <Stack direction="row" spacing={2} alignItems="baseline">
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase' }}>Supplier code</Typography>
                  <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, fontFamily: 'monospace', color: '#c62828' }}>
                    {line.supplierCode || '—'}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase' }}>Description</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#333' }}>{line.description || '—'}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: '0.6rem', color: '#aaa', textTransform: 'uppercase' }}>Unit price</Typography>
                  <Typography sx={{ fontSize: '0.8rem', fontWeight: 700 }}>{fmt(line.unitPrice)}</Typography>
                </Box>
              </Stack>
            </Box>

            {/* Option A — match existing internal code */}
            <Box>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 0.6 }}>
                Match an existing inventory code
              </Typography>
              <Autocomplete
                options={products}
                value={selected}
                onChange={(_, v) => setSelected(v)}
                getOptionLabel={p => `${p.code} — ${p.description}`}
                groupBy={p => p.category || 'Uncategorised'}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={params => (
                  <TextField {...params} size="small" placeholder="Search by code or description…" autoFocus />
                )}
                renderOption={(props, p) => (
                  <li {...props} key={p.id}>
                    <Stack>
                      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, fontFamily: 'monospace', color: FI_ORANGE }}>
                        {p.code}
                      </Typography>
                      <Typography sx={{ fontSize: '0.7rem', color: '#666' }}>{p.description}</Typography>
                    </Stack>
                  </li>
                )}
              />
            </Box>

            <Divider><Typography sx={{ fontSize: '0.66rem', color: '#bbb' }}>or</Typography></Divider>

            {/* Option B — auto match or create (3-letter prefix rule) */}
            {onAutoCreateAndMatch && (
              <Button
                variant="contained" startIcon={<AutoFixHighIcon />}
                onClick={onAutoCreateAndMatch}
                sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: FI_ORANGE,
                  '&:hover': { backgroundColor: '#e65100' } }}
              >
                Auto Create &amp; Match
              </Button>
            )}
            {onAutoCreateAndMatch && (
              <Typography sx={{ fontSize: '0.68rem', color: '#888', fontStyle: 'italic', mt: -0.5 }}>
                Tries remembered mapping, then matches against existing items. If nothing matches, creates a new internal item with a fresh code (e.g. <strong>{mintedCode}</strong>) and links this supplier code to it.
              </Typography>
            )}

            {/* Option C — create new code manually */}
            <Button
              variant="outlined" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}
              sx={{ textTransform: 'none', fontWeight: 700, color: BRAND, borderColor: BRAND,
                '&:hover': { borderColor: BRAND_HOVER, backgroundColor: '#f7f4fc' } }}
            >
              Create new code (new product)
            </Button>

            {onMarkService && (
              <Button
                variant="text" startIcon={<LabelOffIcon />}
                onClick={() => { onMarkService(); onClose(); }}
                sx={{ textTransform: 'none', fontWeight: 600, color: '#546e7a', justifyContent: 'flex-start' }}
              >
                This is a service line (labour — no code required)
              </Button>
            )}

            <FormControlLabel
              control={<Checkbox checked={remember} onChange={e => setRemember(e.target.checked)} sx={{ color: BRAND, '&.Mui-checked': { color: BRAND } }} />}
              label={
                <Typography sx={{ fontSize: '0.78rem' }}>
                  Remember this mapping for <strong>{supplierName}</strong> (auto-match future imports)
                </Typography>
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} sx={{ textTransform: 'none', color: '#666' }}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={!selected} variant="contained"
            sx={{ textTransform: 'none', fontWeight: 700, backgroundColor: BRAND, '&:hover': { backgroundColor: BRAND_HOVER } }}>
            {selected ? `Match to ${selected.code}` : 'Match'}
          </Button>
        </DialogActions>
      </Dialog>

      <AddProductDialog
        open={open && addOpen}
        supplierId={supplierId}
        supplierName={supplierName}
        prefill={{ code: mintedCode, description: line.description, purchasePrice: line.unitPrice }}
        categories={categories}
        onClose={() => setAddOpen(false)}
        onSave={handleProductCreated}
      />
    </>
  );
};

export default ResolveCodeDialog;
